import { Box, Button, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import alertApi from '~/apis/alert';
import { ExportFillIcon } from '~/assets/icons';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { clearInputFilter, exportExcel, handleApiError } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';
import styles from './Alert.module.scss';
import AlertTable from './component/AlertTable';
import { MOCK_ALERT_LEVER, MOCK_ALERT_SUBJECT, MOCK_STATUS } from '~/utils/constants/alert';
import { IAlertSearchPayload } from '~/utils/interface/alert';
import DetailAlertDialog from './component/detail-alert-dialog/DetailAlertDialog';
import { useLocation, useNavigate } from 'react-router-dom';

const cx = classNames.bind(styles);

const initialValues: IAlertSearchPayload = {
  sensor_code: '',
  username: '',
  panel_code: '',
  content: '',
  filter_status: [],
  alert_type: [],
  alert_lever: [],
  alert_subject: [],
  created_at: '',
  alert_object: '',
  status: [],
  created_from: '',
  created_to: '',
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const Alerts = () => {
  const { t } = useTranslation('admin/alert');
  const [alerts, setAlerts] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [timeType] = useState<string>('');
  const [alertLeverOptions] = useState<IMasterDataItem[]>(MOCK_ALERT_LEVER);
  const [filterStatusOptions] = useState<IMasterDataItem[]>(MOCK_STATUS);
  const [alertObjectOptions, setAlertObjectOptions] = useState<IMasterDataItem[]>([]);
  const [alertSubjectOptions] = useState<IMasterDataItem[]>(MOCK_ALERT_SUBJECT);
  const [isOpenDetailAlertDialog, setIsOpenDetailAlertDialog] = useState<boolean>(false);

  const [refreshData, setRefreshData] = useState(false);

  const [currentAlertLever, setCurrentAlertLever] = useState<any>(initialValues.alert_lever);
  const [currentFilterStatus, setCurrentFilterStatus] = useState<any>(initialValues.filter_status);
  const [currentAlertObject, setCurrentAlertObject] = useState<any>(initialValues.alert_object);
  const [currentAlertSubject, setCurrentAlertSubject] = useState<any>(initialValues.alert_subject);
  const [currentCreatedFrom, setCurrentCreatedFrom] = useState<any>(initialValues.created_from);
  const [currentCreatedTo, setCurrentCreatedTo] = useState<any>(initialValues.created_to);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);

  const formatPayload = (values: any, overrideParams: Partial<IAlertSearchPayload> = {}): IAlertSearchPayload => {
    const toArrayOrEmpty = (value: any): string[] => {
      if (Array.isArray(value)) {
        return value.map(String).filter((v) => v !== null && v !== undefined && v !== '');
      }
      if (value === '' || value === undefined || value === null) {
        return [];
      }
      return [String(value)];
    };

    const payload: IAlertSearchPayload = {
      sensor_code: values.sensor_code || '',
      username: values.username || '',
      panel_code: values.panel_code || '',
      content: values.content || '',
      status: toArrayOrEmpty(values.status),

      filter_status: toArrayOrEmpty(values.filter_status),
      alert_type: toArrayOrEmpty(values.alert_type),
      alert_lever: toArrayOrEmpty(values.alert_lever),
      alert_subject: toArrayOrEmpty(values.alert_subject),
      alert_object: values.alert_object || '',
      created_from: values.created_from,
      created_to: values.created_to,
      created_at: values.created_at ? dayjs(values.created_at, 'DD/MM/YYYY').format('YYYY-MM-DD') : undefined,
      page_index: values.page_index,
      page_size: values.page_size,
      order_by: values.order_by,

      ...overrideParams,
    };

    Object.keys(payload).forEach(
      (key) =>
        (payload[key as keyof IAlertSearchPayload] === undefined ||
          payload[key as keyof IAlertSearchPayload] === null) &&
        delete payload[key as keyof IAlertSearchPayload]
    );

    return payload;
  };

  const fetchAlertsData = async (overrideParams: Partial<IAlertSearchPayload> = {}) => {
    try {
      if (!form.values.order_by?.field && !form.values.order_by?.type) {
        delete form.values.order_by;
      }
      const body = formatPayload(form.values, overrideParams);
      const res = await alertApi.postAlert(body);
      return res.data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, items: [] };
    }
  };

  const handleGetListAlerts = async () => {
    const response = await fetchAlertsData();
    const list = response?.items || response?.data || response?.notifications || response?.alerts || [];
    const total = response?.total ?? list.length ?? 0;
    setTotalItems(total);
    setAlerts(list);
  };

  const form = useFormik<IAlertSearchPayload>({
    initialValues,
    onSubmit: () => {
      handleGetListAlerts();
    },
  });

  useEffect(() => {
    if (refreshData) {
      handleGetListAlerts();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  useEffect(() => {
    handleGetListAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size, form.values.order_by]);

  useEffect(() => {
    setCurrentAlertObject(form.values.alert_object ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.alert_object]);

  useEffect(() => {
    form.setFieldValue('alert_lever', currentAlertLever);
    form.setFieldValue('filter_status', currentFilterStatus);
    form.setFieldValue('alert_subject', currentAlertSubject);
    form.setFieldValue('created_from', currentCreatedFrom);
    form.setFieldValue('created_to', currentCreatedTo);

    form.setFieldValue('page_index', 1);

    form.submitForm();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAlertLever, currentFilterStatus, currentAlertSubject, currentCreatedFrom, currentCreatedTo]);

  useEffect(() => {
    const fetchAlertObjects = async () => {
      try {
        const res = await alertApi.getAlertObjects();
        const options: IMasterDataItem[] = (res?.data ?? []).map((str: string) => ({ id: str, name: str }));
        setAlertObjectOptions(options);
      } catch (error) {
        handleApiError(error);
      }
    };
    fetchAlertObjects();
  }, []);
  useEffect(() => {
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  const getAlertObject = (alertSubject: any) => {
    if (alertSubject === 1) return t('alert_subject_options.warning');
    if (alertSubject === 2) return t('alert_subject_options.device');
    if (alertSubject === 3) return t('alert_subject_options.user');
    if (alertSubject === 4) return t('alert_subject_options.agentDistributor');

    return '';
  };

  const exportAlerts = async () => {
    const response = await fetchAlertsData({
      page_index: 1,
      page_size: totalItems,
    });

    const columns = [
      { header: t('exportFile.no'), key: 'no' },
      { header: t('exportFile.alert_lever'), key: 'alert_lever' },
      { header: t('exportFile.alert_subject'), key: 'alert_subject' },
      { header: t('exportFile.alert_object'), key: 'alert_object' },
      { header: t('exportFile.content'), key: 'content' },
      { header: t('exportFile.created_at'), key: 'created_at' },
      { header: t('exportFile.status'), key: 'status' },
    ];

    const data = (response?.data ?? []).map((item: any, index: number) => ({
      ...item,
      no: index + 1,
      alert_subject: getAlertObject(item.alert_subject),
      alert_lever:
        item.alert_lever === 1
          ? t('alert_lever_options.notification')
          : item.alert_lever === 2
          ? t('alert_lever_options.warning')
          : item.alert_lever === 3
          ? t('alert_lever_options.critical')
          : item.alert_lever,
    }));

    exportExcel(columns, data, t('exportFile.fileName'));
  };

  const handleDetailClick = (data: { id: string }) => {
    setSelectedAlertId(data.id);
    setIsOpenDetailAlertDialog(true);
  };
  const handleSetOpenDetail = (open: boolean, needRefreshData?: boolean) => {
    setIsOpenDetailAlertDialog(open);
    if (!open) {
      setSelectedAlertId(null);
    }
    if (needRefreshData) {
      setRefreshData(true);
    }
  };

  const handleClickFilterSearch = () => {
    setCurrentAlertLever([]);
    setCurrentFilterStatus([]);
    setCurrentAlertSubject([]);
    setCurrentCreatedFrom('');
    setCurrentCreatedTo('');
    clearInputFilter();
    form.resetForm();
  };

  const routerLocation = useLocation();
  const { state, pathname } = routerLocation;
  const navigate = useNavigate();

  useEffect(() => {
    const alertId = state?.alertId;
    if (alertId) {
      setIsOpenDetailAlertDialog(true);
      setSelectedAlertId(alertId);
      navigate(pathname, { replace: true });
    }
  }, [state, pathname, navigate]);

  return (
    <Box className={cx('w-full', 'alert-screen')}>
      <Box className={cx('box-container')}>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='alert_lever'>{t('alert_lever')}</FormLabel>
            <BaseSelect
              options={alertLeverOptions}
              value={currentAlertLever}
              placeholder={t('select')}
              name='alert_lever'
              isSearchEnable
              onChange={(e) => {
                const v: any = (e as any).target.value;
                setCurrentAlertLever(Array.isArray(v) ? v.map(String) : String(v) ? [String(v)] : []);
              }}
              displayInvalidValue
              isDisplayAllOption
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='filter_status'>{t('filter_status')}</FormLabel>
            <BaseSelect
              options={filterStatusOptions}
              value={currentFilterStatus}
              placeholder={t('select')}
              name='filter_status'
              isSearchEnable
              onChange={(e) => {
                const v: any = (e as any).target.value;
                setCurrentFilterStatus(Array.isArray(v) ? v.map(String) : String(v) ? [String(v)] : []);
              }}
              displayInvalidValue
              isDisplayAllOption
            />
          </Box>
        </Box>

        <Box className={cx('row-text-field')}>
          <FormLabel htmlFor='alert_subject'>{t('alert_subject')}</FormLabel>
          <BaseSelect
            className={cx('alert-subject-option')}
            options={alertSubjectOptions}
            value={currentAlertSubject}
            placeholder={t('select')}
            name='alert_subject'
            isSearchEnable
            onChange={(e) => {
              const v: any = (e as any).target.value;
              setCurrentAlertSubject(Array.isArray(v) ? v.map(String) : String(v) ? [String(v)] : []);
            }}
            selectAllOptions={(data) => setCurrentAlertSubject(data)}
            multiple
          />
        </Box>
        <Box className={cx('column-text-field-date')}>
          <Typography className={cx('active-day-label')}>{t('activeDay')}</Typography>
          <Box className={cx('date-picker-group')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='created_from'>{t('start')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={currentCreatedFrom}
                dateFormat='DD/MM/YYYY'
                placeholder={t('select')}
                onChange={(date) => {
                  if (!date) {
                    setCurrentCreatedFrom('');
                    return;
                  }

                  const v =
                    timeType === 'month'
                      ? dayjs(date).startOf('month')
                      : timeType === 'year'
                      ? dayjs(date).startOf('year')
                      : dayjs(date);
                  setCurrentCreatedFrom(v.format('YYYY-MM-DDTHH:mm:ss'));
                }}
                showYearPicker={timeType === 'year'}
                showMonthYearPicker={timeType === 'month'}
                calendarProps={currentCreatedTo ? { maxDate: new Date(currentCreatedTo) } : {}}
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='created_to'>{t('end')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                placeholder={t('select')}
                value={currentCreatedTo}
                dateFormat='DD/MM/YYYY'
                onChange={(date) => {
                  if (!date) {
                    setCurrentCreatedTo('');
                    return;
                  }

                  let v = dayjs(date);
                  if (timeType === 'month') v = dayjs(date).endOf('month');
                  else if (timeType === 'year') v = dayjs(date).endOf('year');
                  else v = dayjs(date).endOf('day');

                  setCurrentCreatedTo(v.format('YYYY-MM-DDTHH:mm:ss'));
                }}
                showYearPicker={timeType === 'year'}
                showMonthYearPicker={timeType === 'month'}
                calendarProps={currentCreatedFrom ? { minDate: new Date(currentCreatedFrom) } : {}}
              />
            </Box>
          </Box>
        </Box>
        <Box className=' text-center'>
          <Button
            className='bg-[#ED1002] rounded-[20px] text-white w-[130px] h-[35px] hover:bg-[#ED1002] hover:opacity-80'
            onClick={handleClickFilterSearch}
          >
            {t('clearFilter')}
          </Button>
        </Box>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          my: '14px',
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-2 px-5 justify-between items-center'>
          <Typography className='text-[16px] font-bold text-[white]'>{t('alertList')}</Typography>
          <Box className='flex pr-10'>
            <Button startIcon={<ExportFillIcon />} className='text-white' onClick={exportAlerts}>
              {t('fileExport')}
            </Button>
          </Box>
        </Box>
        <AlertTable
          alerts={alerts}
          form={form}
          setRefreshData={setRefreshData}
          onDetail={handleDetailClick}
          pageSize={form.values.page_size ?? 10}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('alert')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={[
              { id: 10, name: '10' },
              { id: 20, name: '20' },
              { id: 50, name: '50' },
              { id: 100, name: '100' },
              { id: 250, name: '250' },
              { id: 500, name: '500' },
            ]}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('Page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>
      <DetailAlertDialog open={isOpenDetailAlertDialog} data={{ id: selectedAlertId }} setOpen={handleSetOpenDetail} />
    </Box>
  );
};

export default memo(Alerts);
